import { useState, useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import PhoneFrame from "@/components/PhoneFrame";
import SectionLabel from "@/components/SectionLabel";
import {
  Sunrise,
  ClipboardList,
  BarChart3,
  BellRing,
  MessageSquareText,
  Moon,
} from "lucide-react";

const MOMENT_ICONS = [Sunrise, ClipboardList, BarChart3, BellRing, MessageSquareText, Moon];

/**
 * Au quotidien — 2×3 grid of moments on the left, sticky phone on the right.
 * Hover or click a card to switch the phone script. No vertical timeline.
 */
export default function UseCases() {
  const { t, locale } = useLanguage();
  const moments = t("day.moments");
  const scripts = t("day.scripts");
  const stats = t("day.footer_stats");
  const [active, setActive] = useState(0);

  const current = scripts[active];
  const script = useMemo(
    () => ({
      header: { title: "iko", status: locale === "fr" ? "en ligne" : "online" },
      user: current.user,
      ai: current.ai,
      statuses: current.statuses,
      result: {
        title: current.result_title,
        body: current.result_body,
        meta: moments[active].time,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, locale]
  );

  return (
    <section
      id="usecases"
      data-testid="usecases-section"
      className="relative bg-[#FAFAF7] paper-grain border-t border-[#F0EBE0] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-1/4 right-[-10%] w-[600px] h-[600px] halo-soft opacity-30" />
        <div className="absolute bottom-0 left-[-8%] w-[460px] h-[460px] halo-peach opacity-25" />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        {/* Header */}
        <div className="max-w-2xl mb-12 lg:mb-16">
          <SectionLabel>{t("day.eyebrow")}</SectionLabel>
          <h2 className="font-display font-semibold text-4xl lg:text-6xl leading-[1.02] tracking-[-0.034em] text-balance text-[#1A1814] mb-5">
            {t("day.title")}
          </h2>
          <p className="text-base lg:text-lg text-[#6B6862] leading-relaxed max-w-xl">
            {t("day.subtitle")}
          </p>
        </div>

        {/* Grid + phone */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left — 2×3 grid */}
          <div
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4"
            data-testid="day-grid"
          >
            {moments.map((m, i) => {
              const isActive = i === active;
              const Icon = MOMENT_ICONS[i] || Sunrise;
              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`day-moment-${i}`}
                  data-active={isActive}
                  className={`group text-left rounded-2xl p-5 lg:p-6 transition-all duration-400 relative overflow-hidden border ${
                    isActive
                      ? "bg-white border-[#437DEA]/35 shadow-[0_18px_38px_-14px_rgba(67,125,234,0.35)]"
                      : "bg-white/55 border-[#F0EBE0] hover:bg-white hover:border-[#ECE8DD]"
                  }`}
                >
                  {isActive && (
                    <div className="absolute -top-10 -right-10 w-32 h-32 halo-soft opacity-60 pointer-events-none" />
                  )}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-400 ${
                          isActive
                            ? "bg-[#1A1814] text-white"
                            : "bg-[#F4F2EC] border border-[#F0EBE0] text-[#94918A]"
                        }`}
                      >
                        <Icon size={17} strokeWidth={1.8} />
                      </div>
                      <span
                        className={`font-mono text-[10px] font-semibold tracking-[0.14em] ${
                          isActive ? "text-[#437DEA]" : "text-[#94918A]"
                        }`}
                      >
                        {m.time}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-[19px] lg:text-[22px] tracking-[-0.02em] mb-2 text-[#1A1814] leading-tight">
                      {m.title}
                    </h3>
                    <p className="text-[13.5px] lg:text-[14px] leading-relaxed text-[#6B6862] line-clamp-2">
                      {m.body}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right — sticky phone */}
          <div className="lg:col-span-5 lg:sticky lg:top-24 flex justify-center">
            <div className="w-full max-w-[340px]">
              <PhoneFrame
                key={`day-${active}-${locale}`}
                script={script}
                testIdPrefix="day-phone"
              />
            </div>
          </div>
        </div>

        {/* Footer block */}
        <div className="mt-24 lg:mt-28 pt-12 lg:pt-16 border-t border-[#ECE8DD] text-center max-w-3xl mx-auto">
          <h3 className="font-display font-semibold text-3xl lg:text-5xl tracking-[-0.034em] text-balance leading-[1.02] text-[#1A1814] mb-6">
            {t("day.footer_title")}
          </h3>
          <p className="text-lg text-[#6B6862] leading-relaxed max-w-xl mx-auto mb-10">
            {t("day.footer_body")}
          </p>
          {Array.isArray(stats) && stats.length > 0 && (
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3">
              {stats.map((s, i) => (
                <div key={i} className="inline-flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#437DEA]" />
                  <span className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-[#6B6862]">
                    {s}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
